/**
 * LoanRemittanceReportPage — the Loan Remittance generator
 * (PRD_LoanRemittanceReport C1–C3, C8, C9).
 *
 * Same shell as ContributionBasisReportPage: controls, Generate, then the
 * usePdfPreview + PdfPreviewPanel lifecycle — no on-screen table. Reads are
 * the Loans page's (loans + employees), plus each listed employee's PII doc
 * when a member-number column is chosen. A rejected read aborts the whole
 * generate — never a partial document.
 */
import React, { useState, useEffect } from 'react';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { PERMISSIONS } from '../../config/permissions';
import { todayManilaYMD } from '../../utils/periodDate';
import { employeeFullName } from '../../utils/employeeSearch';
import { fetchCompanyLoans } from '../../services/loanService';
import { defaultMonth } from '../../services/contributionBasisReportModel';
import { loanNameOptions, buildLoanRemittanceRows } from '../../services/loanRemittanceReportModel';
import { longMonthLabel } from '../../constants/basisNotes';
import { loadLoanRemittancePdf, PDF_LOAD_ERROR } from '../../services/pdfLoader';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import PdfPreviewPanel from './PdfPreviewPanel';

const inputStyle = {
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--ui-border)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  background: 'var(--ui-surface)',
  fontFamily: 'var(--font-family-primary)',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--ui-text-secondary)',
};

const cardStyle = {
  padding: '1.25rem',
  background: 'var(--ui-surface)',
  border: '1px solid var(--ui-border)',
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

const generatedStamp = (now) =>
  `${now.toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' })} ${now.toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Manila',
    hour: '2-digit',
    minute: '2-digit',
  })}`;

const LoanRemittanceReportPage = () => {
  const { company, hasPermission } = useAuth();
  const toast = useToast();
  const canSeeMemberNumbers = hasPermission(PERMISSIONS.EMPLOYEES_PII_VIEW);

  // C2: default last month; the current month is the ceiling.
  const today = todayManilaYMD();
  const maxMonth = today.slice(0, 7);
  const [month, setMonth] = useState(() => defaultMonth([], today));
  const [options, setOptions] = useState(null); // null while loading
  const [nameKey, setNameKey] = useState('');
  const [idField, setIdField] = useState('');
  const [loadError, setLoadError] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { preview, isMobile, showPreview, clearPreview } = usePdfPreview();

  useEffect(() => {
    if (!company?.id) return;
    fetchCompanyLoans(company.id)
      .then((loans) => {
        const next = loanNameOptions(loans);
        setOptions(next);
        setNameKey(next[0]?.key || '');
        setLoadError(false);
      })
      .catch((err) => {
        console.error('[LoanRemittanceReportPage] loans read failed:', err);
        setLoadError(true);
      });
  }, [company?.id]);

  const handleGenerate = async () => {
    const label = options.find((o) => o.key === nameKey)?.label || '';
    const field = canSeeMemberNumbers && idField ? idField : null;
    setGenerating(true);
    try {
      let pdf;
      try {
        pdf = await loadLoanRemittancePdf();
      } catch (err) {
        console.error('[LoanRemittanceReportPage] PDF chunk load failed:', err);
        clearPreview();
        toast.error(PDF_LOAD_ERROR);
        return;
      }

      // Loans are re-read so a run approved since the page opened is counted.
      let model;
      try {
        const [loans, empSnap] = await Promise.all([
          fetchCompanyLoans(company.id),
          getDocs(query(collection(db, 'companies', company.id, 'employees'), where('role', '==', 'employee'))),
        ]);
        const employees = empSnap.docs
          .filter((d) => !d.data().metadata?.isDeleted)
          .map((d) => ({ id: d.id, employeeId: d.data().employeeId, fullName: employeeFullName(d.data()) }))
          .filter((e) => e.employeeId);
        model = buildLoanRemittanceRows({ loans, employees, nameKey, month });
      } catch (err) {
        console.error('[LoanRemittanceReportPage] loans/employees read failed:', err);
        clearPreview();
        toast.error('Failed to load loans or employees. Please try again.');
        return;
      }

      if (model.rows.length === 0) {
        clearPreview();
        toast.error(`Nothing was deducted for ${label} in ${longMonthLabel(month)} on an approved payroll run.`);
        return;
      }

      // C3: member numbers for the rows' employees only — never for a null docId (C4a).
      let rows = model.rows;
      if (field) {
        try {
          const docIds = [...new Set(rows.map((r) => r.docId).filter(Boolean))];
          const snaps = await Promise.all(
            docIds.map((id) => getDoc(doc(db, 'companies', company.id, 'employees', id, 'pii', id)))
          );
          const numbers = new Map(
            snaps.map((s, i) => [docIds[i], s.exists() ? s.data().governmentIds?.[field] : null])
          );
          rows = rows.map((r) => ({ ...r, govId: (r.docId && numbers.get(r.docId)) || '—' }));
        } catch (err) {
          console.error('[LoanRemittanceReportPage] member number read failed:', err);
          clearPreview();
          toast.error('Failed to load member numbers. Please try again.');
          return;
        }
      }

      showPreview(
        pdf.buildLoanRemittancePdf({
          companyName: company.name,
          label,
          month,
          generatedAt: generatedStamp(new Date()),
          idField: field,
          rows,
          total: model.total,
        }),
        pdf.loanRemittanceFileName({ label, month }),
        `${label} · ${longMonthLabel(month)}`
      );
    } finally {
      setGenerating(false);
    }
  };

  const generateDisabled = generating || loadError || !options || !nameKey || !month;

  return (
    <div>
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ui-primary)', margin: '0 0 0.25rem 0' }}>
        Loan Remittance
      </h1>
      <p style={{ fontSize: '0.875rem', color: 'var(--ui-text-secondary)', margin: '0 0 1.5rem 0' }}>
        What approved payroll runs credited to one loan for one month — per employee, with the total to
        remit. Not a government filing.
      </p>

      {loadError && (
        <div
          style={{
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            background: 'var(--ui-danger-tint)',
            color: 'var(--ui-danger-text)',
            borderRadius: '8px',
            fontSize: '0.875rem',
          }}
        >
          Failed to load loans. Reload the page and try again.
        </div>
      )}

      <div style={cardStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem' }}>
          <label style={labelStyle}>
            LOAN
            <select
              value={nameKey}
              onChange={(e) => setNameKey(e.target.value)}
              disabled={!options || options.length === 0}
              style={inputStyle}
            >
              {options && options.length === 0 && <option value="">No loans yet</option>}
              {(options || []).map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            MONTH
            <input
              type="month"
              value={month}
              max={maxMonth}
              onChange={(e) => setMonth(e.target.value)}
              style={inputStyle}
            />
          </label>

          {canSeeMemberNumbers && (
            <label style={labelStyle}>
              MEMBER NUMBER
              <select value={idField} onChange={(e) => setIdField(e.target.value)} style={inputStyle}>
                <option value="">None</option>
                <option value="sss">SSS number</option>
                <option value="pagibig">Pag-IBIG MID number</option>
              </select>
            </label>
          )}

          <button
            type="button"
            onClick={handleGenerate}
            disabled={generateDisabled}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'var(--ui-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: generateDisabled ? 'not-allowed' : 'pointer',
              opacity: generateDisabled ? 0.6 : 1,
              fontFamily: 'var(--font-family-primary)',
            }}
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>

      <PdfPreviewPanel preview={preview} isMobile={isMobile} title="Loan Remittance preview" />
    </div>
  );
};

export default LoanRemittanceReportPage;
